import { InjectionToken, Type } from '@angular/core';
import type { FilterStateSlot } from '../../state/rates-filter-state.service';

/**
 * Menu-axis Phase 3e — picker component contract.
 *
 * Every picker (FlatChecklistPickerComponent, StateGridPickerComponent,
 * NumericPickerComponent) receives one
 * `PickerContext` via the `POPOVER_CONTEXT` injection token. The
 * picker uses the context to know:
 *   · which dimension it's editing (dimensionKey + label)
 *   · which FilterStateSlot field maps to its selection (field)
 *   · how to read option lists (optionsFn -- closure injected by the
 *     rail consumer so the picker stays decoupled from RatesComponent)
 *   · how to ask the host to dispose itself (closeFn -- Esc / clear /
 *     external close)
 */
export interface PickerContext {
  dimensionKey: string;
  label: string;
  field: keyof FilterStateSlot;
  /** Returns the list of options for this dimension. Re-evaluated
   *  each time the picker opens (3e closures are short-lived). */
  optionsFn: () => string[];
  /** Lets the picker request that the overlay host dispose its
   *  OverlayRef. The host's own backdrop / Esc binding also calls
   *  this. */
  closeFn: () => void;
}

/** Marker interface for any picker component. Picker components
 *  inject POPOVER_CONTEXT to wire their state-read/write logic. */
export interface PickerComponent {
  // No required methods -- the contract is dependency injection.
}

/** DI tokens — the FilterPopoverComponent host provides these via a
 *  sub-injector when it constructs the picker instance. */
export const POPOVER_CONTEXT = new InjectionToken<PickerContext>('PopoverContext');
export const POPOVER_PICKER_COMPONENT = new InjectionToken<Type<PickerComponent>>('PopoverPickerComponent');
